// Lightweight, dependency-free confetti burst for the Poola «موسم البولة» festival
// "opening" moment. Design constraints (see poola-two-side-festival plan, P2-B):
//   • composite-only motion — every particle animates transform + opacity ONLY
//     (translate3d / rotate / scale), never a property that triggers layout/paint;
//   • fully gated behind prefers-reduced-motion — a reduced-motion viewer gets
//     NOTHING (the caller shows a plain opacity fade instead);
//   • self-cleaning — the container removes itself after the burst;
//   • DOM-safe — no-ops under SSR / a missing document.
//
// HARD RULE (community-open honesty): this fires ONLY on the community open moment
// and on collective milestones — NEVER on a member's own claim/purchase.

const FESTIVAL_COLORS = ['#fbbf24', '#a78bfa', '#f472b6', '#34d399', '#60a5fa', '#ffffff'];
const PARTICLE_COUNT = 26;
const DURATION_MS = 1400;

/** True when the viewer asked the OS to reduce motion. Safe under SSR/test envs. */
export function prefersReducedMotion() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Fire a SINGLE confetti burst from the top of the viewport.
 *
 * @param {{ count?: number, durationMs?: number }} [opts]
 * @returns {boolean} true if it fired, false if suppressed (reduced motion / no DOM).
 */
export function fireConfetti({ count = PARTICLE_COUNT, durationMs = DURATION_MS } = {}) {
  // Reduced motion → no confetti at all (the caller falls back to an opacity fade).
  if (prefersReducedMotion()) return false;
  if (typeof document === 'undefined' || !document.body) return false;

  const container = document.createElement('div');
  container.className = 'poola-confetti';
  container.setAttribute('aria-hidden', 'true');
  Object.assign(container.style, {
    position: 'fixed',
    inset: '0',
    pointerEvents: 'none',
    overflow: 'hidden',
    zIndex: '10600',
  });

  for (let i = 0; i < count; i += 1) {
    const particle = document.createElement('span');
    const startXvw = 50 + (Math.random() * 40 - 20); // near screen centre
    const driftVw = Math.random() * 60 - 30; // horizontal drift as it falls
    const size = 6 + Math.random() * 6;
    const spinDeg = Math.random() * 720 - 360;
    const delayMs = Math.random() * 140;
    Object.assign(particle.style, {
      position: 'absolute',
      top: '-16px',
      left: `${startXvw}vw`,
      width: `${size}px`,
      height: `${size * 0.6}px`,
      background: FESTIVAL_COLORS[i % FESTIVAL_COLORS.length],
      borderRadius: '1px',
      opacity: '0',
      willChange: 'transform, opacity',
    });
    container.appendChild(particle);

    // Web Animations API — composite-only keyframes (transform + opacity). Guarded
    // for environments (happy-dom) without element.animate; the particle is still
    // appended and swept up by the timeout below.
    if (typeof particle.animate === 'function') {
      particle.animate(
        [
          { transform: 'translate3d(0, 0, 0) rotate(0deg)', opacity: 1 },
          { transform: `translate3d(${driftVw}vw, 105vh, 0) rotate(${spinDeg}deg)`, opacity: 0 },
        ],
        {
          duration: durationMs,
          delay: delayMs,
          easing: 'cubic-bezier(0.2, 0.6, 0.4, 1)',
          fill: 'forwards',
        },
      );
    }
  }

  document.body.appendChild(container);
  // Sweep up after the longest particle finishes (duration + max delay + buffer).
  window.setTimeout(() => container.remove(), durationMs + 500);
  return true;
}

// Gold "coin" look — a small disc with a highlight, no emoji dependency so it
// renders crisply and on-brand across platforms.
const COIN_GRADIENT = 'radial-gradient(circle at 35% 30%, #fff2c6 0%, #f6c760 45%, #b8860b 100%)';

/** Resolve a burst origin (viewport coords) from an element, an {x,y}, or a fallback. */
function resolveOrigin(anchor) {
  if (anchor && typeof anchor.getBoundingClientRect === 'function') {
    const r = anchor.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
  }
  if (anchor && typeof anchor.x === 'number' && typeof anchor.y === 'number') {
    return { x: anchor.x, y: anchor.y };
  }
  const vw = typeof window !== 'undefined' ? window.innerWidth : 360;
  return { x: vw / 2, y: 80 };
}

/**
 * Fire a SINGLE coin "fountain" anchored to an element (e.g. the completed
 * offer's «مكتمل ✓» badge) — coins arc up and out of the origin, then fall.
 * Unlike fireConfetti (viewport-top sprinkle), this reads as THIS card winning:
 * the reward flowing back as بولكريديت. Same guarantees as fireConfetti —
 * composite-only motion (transform + opacity), reduced-motion suppressed,
 * self-cleaning, DOM-safe.
 *
 * @param {Element|{x:number,y:number}|null} anchor origin (element rect centre, a point, or fallback to top-centre)
 * @param {{ count?: number, durationMs?: number }} [opts]
 * @returns {boolean} true if it fired, false if suppressed.
 */
export function fireRewardBurst(anchor, { count = 24, durationMs = 1150 } = {}) {
  if (prefersReducedMotion()) return false;
  if (typeof document === 'undefined' || !document.body) return false;

  const { x: ox, y: oy } = resolveOrigin(anchor);

  const container = document.createElement('div');
  container.className = 'poola-reward-burst';
  container.setAttribute('aria-hidden', 'true');
  Object.assign(container.style, {
    position: 'fixed',
    inset: '0',
    pointerEvents: 'none',
    overflow: 'hidden',
    zIndex: '10600',
  });

  for (let i = 0; i < count; i += 1) {
    const coin = document.createElement('span');
    const size = 8 + Math.random() * 7;
    // Fan mostly UPWARD out of the origin (base -90°, ±~80° spread).
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * (Math.PI * 0.9);
    const speed = 55 + Math.random() * 95;
    const dx1 = Math.cos(angle) * speed; // apex horizontal
    const dy1 = Math.sin(angle) * speed; // apex vertical (negative = up)
    const dx2 = dx1 * 1.7 + (Math.random() - 0.5) * 34; // drift further on the way down
    const dy2 = Math.abs(dy1) * 0.5 + 130 + Math.random() * 90; // fall below the origin
    const spin = Math.random() * 560 - 280;
    const delayMs = Math.random() * 120;
    Object.assign(coin.style, {
      position: 'absolute',
      left: `${ox}px`,
      top: `${oy}px`,
      width: `${size}px`,
      height: `${size}px`,
      marginLeft: `${-size / 2}px`,
      marginTop: `${-size / 2}px`,
      borderRadius: '50%',
      background: COIN_GRADIENT,
      boxShadow: '0 1px 2px rgba(184, 134, 11, 0.55)',
      opacity: '0',
      willChange: 'transform, opacity',
    });
    container.appendChild(coin);

    // Composite-only 3-keyframe arc (transform + opacity). Guarded for envs
    // without element.animate (happy-dom); coins are still swept up below.
    if (typeof coin.animate === 'function') {
      coin.animate(
        [
          { transform: 'translate3d(0, 0, 0) rotate(0deg)', opacity: 1, offset: 0 },
          {
            transform: `translate3d(${dx1}px, ${dy1}px, 0) rotate(${spin * 0.5}deg)`,
            opacity: 1,
            offset: 0.4,
          },
          {
            transform: `translate3d(${dx2}px, ${dy2}px, 0) rotate(${spin}deg)`,
            opacity: 0,
            offset: 1,
          },
        ],
        {
          duration: durationMs,
          delay: delayMs,
          easing: 'cubic-bezier(0.3, 0.6, 0.4, 1)',
          fill: 'forwards',
        },
      );
    }
  }

  document.body.appendChild(container);
  window.setTimeout(() => container.remove(), durationMs + 400);
  return true;
}
